export interface ScreenPoint {
  x: number;
  y: number;
}

// Ten CSS pixels absorb ordinary finger jitter without turning a deliberate
// short swipe into a walk command.
export const FLOOR_TAP_MOVE_DISTANCE_THRESHOLD = 10;

interface PointerGesture {
  start: ScreenPoint;
  involvedMultiplePointers: boolean;
  startedWhileViewportMoving: boolean;
}

/**
 * Keeps the input state needed to decide whether PIXI's `pointertap` really was
 * a tap. It deliberately does not track elapsed time: a stationary long press
 * is still a valid walk command.
 */
export class FloorTapGestureGuard {
  private readonly activePointers = new Set<number>();
  private readonly gestures = new Map<number, PointerGesture>();
  private viewportMoving = false;

  viewportMoved(): void {
    this.viewportMoving = true;
  }

  viewportMoveEnded(): void {
    this.viewportMoving = false;
  }

  pointerDown(pointerId: number, global: ScreenPoint): void {
    // A gesture record lives until its tap is consumed, but PIXI does not emit
    // `pointertap` for every press (a drag that ends over another target does
    // not tap). Once no finger is down, any leftover record is stale.
    if (this.activePointers.size === 0) this.gestures.clear();

    this.activePointers.add(pointerId);
    this.gestures.set(pointerId, {
      start: { x: global.x, y: global.y },
      involvedMultiplePointers: false,
      startedWhileViewportMoving: this.viewportMoving,
    });

    if (this.activePointers.size <= 1) return;
    for (const activePointerId of this.activePointers) {
      const gesture = this.gestures.get(activePointerId);
      if (gesture) gesture.involvedMultiplePointers = true;
    }
  }

  pointerUp(pointerId: number): void {
    this.activePointers.delete(pointerId);
  }

  pointerCancelled(pointerId: number): void {
    this.activePointers.delete(pointerId);
    this.gestures.delete(pointerId);
  }

  finishTap(pointerId: number): void {
    this.gestures.delete(pointerId);
  }

  consumeValidTap(pointerId: number, global: ScreenPoint): boolean {
    const gesture = this.gestures.get(pointerId);
    this.gestures.delete(pointerId);
    if (!gesture) return false;
    if (gesture.involvedMultiplePointers || gesture.startedWhileViewportMoving) {
      return false;
    }

    const dx = global.x - gesture.start.x;
    const dy = global.y - gesture.start.y;
    return dx * dx + dy * dy <= FLOOR_TAP_MOVE_DISTANCE_THRESHOLD ** 2;
  }
}
